import { useTranslation } from 'react-i18next';
import { computeDeviations } from '../../utils/statsEngine';
import type { Measurement } from '../../services/data';

interface StatsData {
  count: number;
  min: number | null;
  max: number | null;
  avg: number | null;
  std_dev: number | null;
  amplitude: number | null;
}

interface ProbeStatsProps {
  stats: StatsData | null;
  measurements: Measurement[]; // for deviation calculation (sorted ASC by timestamp)
  unit: string;
  thresholds: {
    alarmLow?: number | null;
    alarmHigh?: number | null;
  };
  avgIntervalMinutes?: number | null;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h${remMins}min` : `${hrs}h`;
}

function formatInterval(minutes: number | null | undefined): string {
  if (minutes == null) return '--';
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  return `${Math.round(minutes)}min`;
}

function formatDeviation(high: number, low: number, alarmHigh: number | null | undefined, alarmLow: number | null | undefined): string {
  const parts: string[] = [];
  if (alarmHigh != null) parts.push(high > 0 ? `+${high.toFixed(1)}°C` : '0.0°C');
  if (alarmLow != null) parts.push(low > 0 ? `-${low.toFixed(1)}°C` : '0.0°C');
  return parts.join(' | ') || '--';
}

export default function ProbeStats({ stats, measurements, unit, thresholds, avgIntervalMinutes }: ProbeStatsProps) {
  const { t } = useTranslation();

  const hasThresholds = thresholds.alarmLow != null || thresholds.alarmHigh != null;

  const deviations = hasThresholds && measurements.length > 0
    ? computeDeviations(
        measurements.map((m) => Number(m.value_calibrated)),
        measurements.map((m) => new Date(m.timestamp).getTime() / 1000),
        {
          alarmHigh: thresholds.alarmHigh ?? null,
          alarmLow: thresholds.alarmLow ?? null,
        },
      )
    : null;

  if (!stats) {
    return (
      <p className="text-sm text-gray-400 italic py-2">{t('common.noData')}</p>
    );
  }

  const StatCell = ({ label, value }: { label: string; value: string }) => (
    <div className="stat-item">
      <span className="text-xs text-gray-500">{label}:</span>
      <span className="text-sm font-semibold text-gray-900 ml-1">{value}</span>
    </div>
  );

  const devCount = deviations?.count ?? 0;
  const dash = '--';

  return (
    <div className="stats-row flex flex-col sm:flex-row gap-3 p-4">
      {/* MESURES */}
      <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3">
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">{t('logger.sectionMeasures')}</h4>
        <div className="grid grid-cols-2 gap-y-2 gap-x-4">
          <StatCell label={t('logger.statCount')} value={String(stats.count)} />
          <StatCell label={t('logger.avg')} value={stats.avg != null ? `${stats.avg.toFixed(1)}${unit}` : dash} />
          <StatCell label={t('logger.min')} value={stats.min != null ? `${stats.min.toFixed(1)}${unit}` : dash} />
          <StatCell label={t('logger.stdDev')} value={stats.std_dev != null ? `${stats.std_dev.toFixed(2)}${unit}` : dash} />
          <StatCell label={t('logger.max')} value={stats.max != null ? `${stats.max.toFixed(1)}${unit}` : dash} />
          <StatCell label={t('logger.avgInterval')} value={formatInterval(avgIntervalMinutes)} />
        </div>
      </div>

      {/* DÉVIATIONS */}
      <div className={`flex-1 border rounded-lg p-3 ${devCount > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
        <h4 className={`text-xs font-bold uppercase tracking-wide mb-2 ${devCount > 0 ? 'text-red-700' : 'text-gray-700'}`}>
          {t('logger.sectionDeviations')}
        </h4>
        <div className="grid grid-cols-2 gap-y-2 gap-x-4">
          <StatCell label={t('logger.deviationCount')} value={devCount > 0 ? String(devCount) : dash} />
          <StatCell label={t('logger.deviationMaxDuration')} value={devCount > 0 ? formatDuration(deviations!.maxTimeSeconds) : dash} />
          <StatCell label={t('logger.deviationTotalDuration')} value={devCount > 0 ? formatDuration(deviations!.totalTimeSeconds) : dash} />
          <StatCell
            label={t('logger.deviationAvgAmp')}
            value={devCount > 0 ? formatDeviation(deviations!.avgDeviationHigh, deviations!.avgDeviationLow, thresholds.alarmHigh, thresholds.alarmLow) : dash}
          />
          <StatCell label={t('logger.deviationAvgDuration')} value={devCount > 0 ? formatDuration(deviations!.avgTimeSeconds) : dash} />
          <StatCell
            label={t('logger.deviationMaxAmp')}
            value={devCount > 0 ? formatDeviation(deviations!.maxDeviationHigh, deviations!.maxDeviationLow, thresholds.alarmHigh, thresholds.alarmLow) : dash}
          />
        </div>
      </div>
    </div>
  );
}
